import { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Check, Crown, Star, ArrowLeft, Shield, Lock, Eye, Zap, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import SEOHead from '../components/SEOHead';
import { useTranslation } from 'react-i18next';

const PLANS = {
  premium: {
    key: 'premium',
    icon: Star,
    color: '#6E1A24',
    features: [
      { icon: Heart, key: 'pricing.feat_unlimited_likes' },
      { icon: Sparkles, key: 'pricing.feat_advanced_filters' },
      { icon: Shield, key: 'pricing.feat_priority_support' },
    ],
  },
  gold: {
    key: 'gold',
    icon: Crown,
    color: '#D4AF37',
    features: [
      { icon: Check, key: 'pricing.feat_everything_premium' },
      { icon: Eye, key: 'pricing.feat_see_who_liked' },
      { icon: TrendingUp, key: 'pricing.feat_higher_visibility' },
      { icon: Zap, key: 'pricing.feat_exclusive' },
    ],
  },
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const currentPlan = useMemo(() => user?.subscription?.plan || user?.current_plan || 'free', [user]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="pricing-page">
      <SEOHead title="Plans & Pricing" noindex path="/pricing" />
      <Navigation />

      <div className="pt-20 sm:pt-8 pb-24 sm:pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm text-[#6C6C67] hover:text-[#2D2D2A] transition-colors mb-6"
              data-testid="pricing-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>
            <h1 className="text-2xl sm:text-3xl font-serif font-medium text-[#2D2D2A] mb-2" data-testid="pricing-title">
              {t('pricing.title')}
            </h1>
            <p className="text-sm text-[#6C6C67] max-w-md mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
            {/* Premium Card */}
            <PlanCard
              plan={PLANS.premium}
              currentPlan={currentPlan}
              t={t}
              onSelect={() => navigate('/checkout?plan=premium')}
            />

            {/* Gold Card */}
            <PlanCard
              plan={PLANS.gold}
              currentPlan={currentPlan}
              t={t}
              onSelect={() => navigate('/checkout?plan=gold')}
              highlighted={currentPlan === 'premium'}
            />
          </div>

          {/* Gold nudge */}
          {currentPlan !== 'gold' && (
            <p className="text-center text-sm text-[#6C6C67] italic" data-testid="pricing-gold-nudge">
              {t('pricing.gold_nudge')}
            </p>
          )}

          {/* Security line */}
          <div className="flex items-center justify-center gap-4 mt-6 text-xs text-[#9C9C97]">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> {t('pricing.secure')}
            </div>
            <div className="flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> {t('pricing.cancel_anytime')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, currentPlan, t, onSelect, highlighted = false }) {
  const isCurrentPlan = currentPlan === plan.key;
  const isDowngrade = (currentPlan === 'gold' && plan.key === 'premium');
  const isDisabled = isCurrentPlan || isDowngrade;
  const isGold = plan.key === 'gold';
  const PlanIcon = plan.icon;

  return (
    <div
      className={`relative rounded-3xl border-2 overflow-hidden transition-all ${
        isGold
          ? highlighted
            ? 'border-[#D4AF37] shadow-lg shadow-[#D4AF37]/15 ring-1 ring-[#D4AF37]/20'
            : 'border-[#D4AF37]/40 shadow-md'
          : 'border-[#E5E3DB] shadow-sm'
      }`}
      data-testid={`pricing-card-${plan.key}`}
    >
      {/* Popular badge for Gold */}
      {isGold && !isCurrentPlan && (
        <div className="absolute top-0 right-0 bg-[#D4AF37] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl" data-testid="pricing-popular-badge">
          {t('pricing.most_popular')}
        </div>
      )}

      {/* Card header */}
      <div
        className="p-6 pb-4"
        style={{
          background: isGold
            ? 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))'
            : 'linear-gradient(135deg, rgba(110,26,36,0.06), rgba(110,26,36,0.01))',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              isGold
                ? 'bg-gradient-to-br from-[#D4AF37] to-[#B8962E]'
                : 'bg-gradient-to-br from-[#6E1A24] to-[#8A2331]'
            }`}
          >
            <PlanIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-serif font-semibold text-[#2D2D2A]">
              {t(`pricing.plan_${plan.key}`)}
            </h2>
            {isCurrentPlan && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#6C6C67] bg-[#E5E3DB] px-2 py-0.5 rounded-full">
                {t('pricing.current_plan')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold" style={{ color: plan.color }}>
            {'\u20AC'}{t(`pricing.price_${plan.key}`)}
          </span>
          <span className="text-sm text-[#9C9C97]">{t('pricing.per_month')}</span>
        </div>
      </div>

      {/* Features */}
      <div className="px-6 py-5 border-t border-[#E5E3DB]/60">
        <ul className="space-y-3">
          {plan.features.map((feat) => {
            const FeatIcon = feat.icon;
            return (
              <li key={feat.key} className="flex items-center gap-2.5 text-sm text-[#2D2D2A]">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${plan.color}15` }}
                >
                  <FeatIcon className="w-3 h-3" style={{ color: plan.color }} />
                </div>
                {t(feat.key)}
              </li>
            );
          })}
        </ul>
      </div>

      {/* CTA */}
      <div className="px-6 pb-6">
        {isCurrentPlan ? (
          <div
            className="w-full py-3 rounded-full text-center text-sm font-medium bg-[#F5F2EB] text-[#9C9C97]"
            data-testid={`pricing-current-${plan.key}`}
          >
            {t('pricing.current_plan')}
          </div>
        ) : isDowngrade ? (
          <div className="w-full py-3 rounded-full text-center text-sm text-[#9C9C97] bg-[#F5F2EB]">
            {t('pricing.included')}
          </div>
        ) : (
          <Button
            onClick={onSelect}
            className={`w-full rounded-full py-3 text-sm font-semibold transition-all hover:shadow-lg ${
              isGold
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:from-[#C9A432] hover:to-[#A88929] text-white'
                : 'bg-[#6E1A24] hover:bg-[#8A2331] text-white'
            }`}
            data-testid={`pricing-select-${plan.key}`}
          >
            {currentPlan === 'premium' && isGold
              ? t('pricing.go_gold')
              : t('pricing.select_plan', { plan: t(`pricing.plan_${plan.key}`) })}
          </Button>
        )}
      </div>
    </div>
  );
}
